import { Shield, Target, Zap, TrendingUp, MessageCircle } from 'lucide-react'
import Button from '../ui/Button.jsx'
import { useScrollReveal, useStaggerReveal } from '../../hooks/useScrollReveal.js'
import { useParallax } from '../../hooks/useParallax.js'

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

/* ─── Data ──────────────────────────────────────────────────── */
const ONBOARDING_STEPS = [
  {
    n:    '1',
    title: 'Download the DecidA app',
    body:  'Download the DecidA merchant app and complete a quick registration. No forms, no downloads beyond the app.',
  },
  {
    n:    '2',
    title: 'Set up your profile',
    body:  'Add your business name, category, and service area. Takes less than two minutes.',
  },
  {
    n:    '3',
    title: 'Set your area & availability',
    body:  'Tell us your primary service area and when you\'re available: now, today, or by appointment.',
  },
  {
    n:    '4',
    title: 'Receive qualified requests',
    body:  'You get real buyer requests that match what you do. Reply YES · LATER · DECLINE directly in the app.',
  },
]

const PROTECTIONS = [
  { icon: Shield,     title: 'Not too many at once', body: 'We limit how many requests you get per hour so it never piles up.' },
  { icon: Target,     title: 'Only what fits you',  body: 'You only see requests that match what you do and where you work.' },
  { icon: Zap,        title: 'One-tap responses',  body: 'YES · LATER · DECLINE. No long replies needed.' },
  { icon: TrendingUp, title: 'Reliability score',  body: 'Responsive merchants rank higher and receive more missions.' },
]

/* ─── Section ───────────────────────────────────────────────── */
export default function ForMerchants() {
  const headerRef      = useScrollReveal()
  const onboardingRef  = useScrollReveal({ threshold: 0.1 })
  const protectionsRef = useStaggerReveal()
  const bannerRef      = useScrollReveal()
  const bgOrbRef       = useParallax({ speed: -0.2 })

  return (
    <section id="for-merchants" className="py-24 bg-[#f8fafa] relative overflow-hidden">
      {/* Parallax ambient orb */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          ref={bgOrbRef}
          className="parallax-layer absolute -bottom-24 -right-24 w-[480px] h-[480px]
                     rounded-full bg-primary/6 blur-[100px]"
        />
      </div>
      <div className="section-container relative z-10">

        {/* ── Header ───────────────────────────────────── */}
        <div ref={headerRef} className="reveal max-w-2xl mb-14">
          <p className="eyebrow mb-3">
            <span className="w-5 h-px bg-primary" />
            For Merchants
          </p>
          <h2
            className="font-extrabold text-brand-dark mb-4"
            style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)' }}
          >
            Qualified requests.<br className="hidden sm:block" />
            Straight to your app.
          </h2>
          <p className="text-base text-brand-gray leading-relaxed">
            Every day, buyers in Accra are looking for someone like you.
            DecidA sends you their requests directly in the app, so you never miss a lead.
          </p>
        </div>

        {/* ── Two-column grid ───────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">

          {/* Onboarding steps */}
          <div ref={onboardingRef} className="reveal scale-in card p-8">
            <p className="text-xs font-bold tracking-widest uppercase text-primary mb-6">
              QR-First Onboarding: 4 Steps
            </p>
            <ol className="flex flex-col gap-6">
              {ONBOARDING_STEPS.map(({ n, title, body }) => (
                <li key={n} className="flex gap-4">
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full bg-primary
                               text-white text-xs font-bold flex items-center
                               justify-center shadow-sm transition-transform
                               duration-200 hover:scale-110"
                  >
                    {n}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-brand-dark mb-0.5">{title}</p>
                    <p className="text-sm text-brand-gray leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-8 pt-6 border-t border-gray-100 flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-brand-gray">
                Runs entirely in the{' '}
                <span className="font-semibold text-brand-dark">DecidA merchant app.</span>{' '}
                Simple, fast, and built for your workflow.
              </p>
            </div>
          </div>

          {/* Merchant protections — staggered */}
          <div ref={protectionsRef} className="flex flex-col gap-4">
            <p className="text-xs font-bold tracking-widest uppercase text-primary">
              Built to protect your experience
            </p>
            {PROTECTIONS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="reveal flex gap-4 bg-white rounded-xl border border-gray-100
                           shadow-sm p-5 items-start
                           transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="w-9 h-9 rounded-xl bg-primary-light flex items-center
                                justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4.5 h-4.5 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-bold text-brand-dark mb-0.5">{title}</p>
                  <p className="text-sm text-brand-gray leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Pilot banner ─────────────────────────────── */}
        <div
          ref={bannerRef}
          className="reveal rounded-2xl p-8 flex flex-col sm:flex-row
                     sm:items-center sm:justify-between gap-6
                     transition-shadow duration-300 hover:shadow-xl"
          style={{ backgroundColor: '#1F2933' }}
        >
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-primary mb-1">
              Accra · Invite-Based
            </p>
            <p className="text-base font-semibold text-white mb-1">
              Target: 2,000 – 8,000 active merchants in the first 90 days.
            </p>
            <p className="text-sm text-gray-400">
              Priority categories: phone repairs, beauty, fashion, tradespeople, events.
            </p>
          </div>
          <Button
            variant="primary"
            className="btn-shimmer flex-shrink-0 !px-7 !py-3.5 hover:scale-[1.03] active:scale-[0.98]"
            onClick={() => scrollTo('join')}
          >
            Request Early Access
          </Button>
        </div>

      </div>
    </section>
  )
}
